import AddCircleIcon from "@mui/icons-material/AddCircle"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import {
  DeviceSetup,
  Examination,
  History,
  Status,
  Task,
  TaskStep,
  TestData,
  User,
} from "graphql/types"
import { useEffect, useMemo, useState } from "react"

import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useParams } from "react-router"
import { v4 } from "uuid"
import { DataInputDialog } from "./DataInputDialog"

import { SetDateTimeDialog } from "components/SetDateTimeDialog/SetDateTimeDialog"

import dayjs from "dayjs"
import { emptyPerson } from "store/store"
import { allExaminations } from "utils/allExaminations"
import { createChangeHistoryEntry, createNewHistoryEntry } from "utils/createHistory"
import { calculateTotalTimeInSeconds } from "utils/dateTimeUtils"
import { getIconForTaskStep, getTextForTaskStep, onlyOnceTask } from "utils/taskSteps"
import { useLocalStorage } from "utils/useLocalStorage"

type TaskContentProps = {
  task: Task
}

export const TaskContent = (props: TaskContentProps) => {
  const { task } = props
  const [testDatas, setTestDatas] = useState<TestData[]>([])
  const [testDataID, setTestDataID] = useState<string>("")
  const [openDataInput, setOpenDataInput] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [dataToDelete, setDataToDelete] = useState<TestData>()
  const updateExamination = useStoreActions((state) => state.updateExamination)
  const projectsState = useStoreState((state) => state.projects)
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const { projectID, examinationID } = useParams()
  const [editTaskStep, setEditTaskStep] = useState<TaskStep>(task.taskSteps[0])
  const [openTimeDialog, setOpenTimeDialog] = useState(false)
  const [tasksDisabled, setTasksDisabled] = useState<TaskStep[]>([])

  const project = projectsState.find((project) => project?.id === projectID)

  const examination: Examination | undefined = useMemo(() => {
    if (!project) return undefined
    return allExaminations(project).find((item) => item.id === examinationID)
  }, [project, examinationID])

  const emptyTestData: TestData = {
    id: "",
    name: "",
    description: "",
    image: [],
    type: editTaskStep,
    beginTime: "",
    endTime: "",
    helper: [],
    ambientConditions: undefined,
    deviceTestData: [],
    visualCheck: false,
    witnesses: [],
    createdAt: dayjs().toISOString(),
    createdBy: user,
  }

  const [newData, setNewData] = useState<TestData>(emptyTestData)
  const [newDeviceSetup, setNewDeviceSetup] = useState<DeviceSetup[]>([])
  const [newExamination, setNewExamination] = useState<Examination | undefined>(examination)

  useEffect(() => {
    const testData = testDatas.find((data) => data.id === testDataID)
    if (testData) {
      setNewData(testData)
    }
  }, [testDataID, testDatas])

  useEffect(() => {
    const disabledTasks = onlyOnceTask.filter((item) =>
      task.testDatas.flatMap((data) => data?.type).includes(item)
    )
    setTasksDisabled(disabledTasks)

    // Wenn das aktuell ausgewählte editTaskStep disabled ist, wähle das erste verfügbare aus
    if (disabledTasks.includes(editTaskStep)) {
      const firstAvailable = task.taskSteps.find((item) => !disabledTasks.includes(item))
      if (firstAvailable) {
        setEditTaskStep(firstAvailable)
      }
    }
  }, [examination, task.testDatas, editTaskStep, task.taskSteps])

  useEffect(() => {
    if (examination && examination.deviceSetup) {
      const newItem = examination.deviceSetup.filter(
        (device): device is DeviceSetup => device !== null
      )
      setNewDeviceSetup(newItem)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examination?.deviceSetup])

  useEffect(() => {
    if (examination) {
      setNewExamination(examination)
    }
  }, [examination])

  const handleAddNewData = () => {
    setNewData(emptyTestData)
    setOpenDataInput(true)
  }

  useEffect(() => {
    if (examination && examination.tasks) {
      const currentTask: Task | undefined = examination.tasks.find((item) => item.id === task.id)
      if (currentTask && currentTask.testDatas) {
        setTestDatas(currentTask.testDatas.filter((data): data is TestData => data !== null))
      }
    }
  }, [examination, task.id])

  const handleCloseDataInput = () => {
    setOpenDataInput(false)
    setTestDataID("")
  }

  const addTestData = () => {
    newData.type = newData.id !== "" ? newData.type : editTaskStep
    newData.id = newData.id !== "" ? newData.id : v4()

    if (newExamination && projectID) {
      const newDatas: TestData[] = [...testDatas]
      const history: History = {
        id: v4(),
        createdAt: new Date().toISOString(),
        createdBy: user,
        description: [],
      }

      const index = testDatas.findIndex((item) => item.id === newData.id)

      if (index !== -1) {
        // add history entry for changing datas
        history.description = createChangeHistoryEntry(newDatas[index], newData, editTaskStep)
        newDatas[index] = newData
      } else {
        // create first history entry with new datas
        newDatas.push(newData)
        history.description = createNewHistoryEntry(newData, editTaskStep)
      }
      const updatedExamination = {
        ...newExamination,
        deviceSetup: newDeviceSetup,
        history: [...(newExamination.history || []), history],
        tasks:
          newExamination.tasks?.map((item) =>
            item.id === task.id
              ? {
                  ...item,
                  testDatas: newDatas,
                  workingTime: item.taskSteps.includes(TaskStep.WAITING_TIME)
                    ? calculateTotalTimeInSeconds(newDatas)
                    : item.workingTime,
                }
              : item
          ) || [],
      }

      updateExamination(updatedExamination)

      if (onlyOnceTask.includes(editTaskStep)) {
        const firstUnique = task.taskSteps.find(
          (item) => !tasksDisabled.includes(item) && item !== editTaskStep
        )
        if (firstUnique) setEditTaskStep(firstUnique)
      }
    }
  }

  const handleDelete = () => {
    if (examination && projectID && dataToDelete) {
      if (onlyOnceTask.includes(dataToDelete.type)) {
        const firstUnique = task.taskSteps.find(
          (item) => !tasksDisabled.includes(item) && item !== dataToDelete.type
        )
        if (firstUnique) setEditTaskStep(firstUnique)
      }

      setTestDatas(testDatas.filter((d) => d.id !== dataToDelete.id))

      const history: History = {
        id: v4(),
        createdAt: new Date().toISOString(),
        createdBy: user,
        description: [`Gelöscht: ${dataToDelete.name}`, `Daten: ${dataToDelete.description}`],
      }

      const newDatas = testDatas.filter((d) => d.id !== dataToDelete.id)

      const updatedExamination = {
        ...examination,
        history: [...(examination.history || []), history],
        tasks:
          examination.tasks?.map((item) =>
            item.id === task.id
              ? {
                  ...item,
                  testDatas: newDatas,
                  workingTime: item.taskSteps.includes(TaskStep.WAITING_TIME)
                    ? calculateTotalTimeInSeconds(newDatas)
                    : item.workingTime,
                }
              : item
          ) || [],
      }

      updateExamination(updatedExamination)
    }
    handleCloseDeleteDialog()
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setDataToDelete(undefined)
  }

  const handleOpenDeleteDialog = (data: TestData) => {
    setOpenDeleteDialog(true)
    setDataToDelete(data)
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2">{task.info}</Typography>
        {/* <IconButton size="large" onClick={() => setOpenTimeDialog(true)}>
          <CalendarMonthIcon fontSize="inherit" />
        </IconButton> */}
      </Box>
      {testDatas.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {testDatas.map((data) => (
            <Card key={data.id}>
              <CardHeader
                title={data.name === "" ? getTextForTaskStep(data.type) : data.name}
                avatar={<Avatar variant="square">{getIconForTaskStep(data.type)}</Avatar>}
                action={
                  <>
                    <IconButton
                      aria-label="Löschen"
                      size="small"
                      onClick={() => handleOpenDeleteDialog(data)}
                      disabled={task.status === Status.DONE}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      aria-label="bearbeiten"
                      size="small"
                      onClick={() => {
                        setTestDataID(data.id)
                        setOpenDataInput(true)
                        setEditTaskStep(data.type)
                      }}
                      disabled={task.status === Status.DONE}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </>
                }
              />
            </Card>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControl fullWidth>
          <Select
            size="small"
            variant="outlined"
            value={editTaskStep}
            onChange={(e) => {
              setEditTaskStep(e.target.value as TaskStep)
            }}
          >
            {task.taskSteps.map((item) => (
              <MenuItem key={item} value={item} disabled={tasksDisabled.includes(item)}>
                {getTextForTaskStep(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          size="large"
          onClick={handleAddNewData}
          disabled={task.status === Status.DONE || editTaskStep === undefined}
        >
          <AddCircleIcon fontSize="inherit" />
        </IconButton>
      </Box>

      {editTaskStep && examination && (
        <DataInputDialog
          open={openDataInput}
          onClose={handleCloseDataInput}
          addData={addTestData}
          testData={newData}
          setTestData={setNewData}
          deviceSetup={newDeviceSetup}
          setDeviceSetup={setNewDeviceSetup}
          examination={newExamination}
          setExamination={setNewExamination}
        />
      )}

      <DeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDelete}
      />

      <SetDateTimeDialog open={openTimeDialog} onClose={() => setOpenTimeDialog(false)} />
    </>
  )
}
